import {addMinutes, formatDateToTime} from 'helpers/dateFormat';
import {isEqualDates} from 'helpers/compareDates';
import {makeAutoObservable, observable} from 'mobx';
import {Class} from 'models/Class';
import ClassApiManager, {
  DeleteTypeId,
} from 'services/API/ClassApi/ClassApiManager';
import strings from 'translations';
import moment from 'moment';
import constants from '../constants';
import {dateFormatForApiRequest} from 'helpers/dateFormatForApiRequest';
import {PopUpStore} from './PopUpStore';

export enum LessonStatus {
  notStarted = 'NOT_STARTED',
  inProgress = 'IN_PROGRESS',
  completed = 'COMPLETED',
  error = 'ERROR',
  cancelled = 'CANCELLED',
}

export interface StudentScheduleFormat {
  sessionId: string;
  scheduleId: string;
  title: string;
  classMarking: string;
  scheduledTime: string;
  classDate: string;
  classEndTime: string;
  status: LessonStatus;
  isRecordingNeeded: boolean;
}

export interface SectionListFormat {
  title: string;
  data: StudentScheduleFormat[];
}

export interface EventList {
  start: Date;
  end: Date;
  title: string;
}

export interface ClassStoreType {
  currentDate: string;
  dayIndex: number;
  weekNumber: number;
  lessonList: SectionListFormat[];
  eventList: EventList[];
  analyticLessonList: StudentScheduleFormat[];
  isRecordingRequest: boolean;

  getClasses: () => Promise<void>;
  setCurrentDate(date: string): void;
  setCurrentDateByDay(day: Date): void;
  setDayIndex(day: number): void;
  setWeekNumber(week: number): void;
  setLessonList(list: SectionListFormat[]): void;
  setAllClasses(classes: Class[]): void;
  hasClassesOnDay: (day: Date) => boolean;
  getConferenceNameForId: (id: string) => string;
  mapAndSetEventListForWeb: () => void;
  deleteLesson(idToDelete: string, deleteTypeId: DeleteTypeId): void;
  getRecording(classId: string): void;
}

export class ClassStore {
  private classApiManager = new ClassApiManager();
  private allClasses: Class[] = [];
  private popUpStore: PopUpStore;

  eventList: EventList[] = [];
  currentDate: string = '';
  dayIndex: number = 0;
  weekNumber: number = 1;
  lessonList: SectionListFormat[] = [];
  analyticLessonList: StudentScheduleFormat[] = [];
  isRecordingRequest: boolean = false;

  constructor(popUpStore: PopUpStore) {
    this.popUpStore = popUpStore;

    makeAutoObservable(this, {
      lessonList: observable.shallow,
    });
  }

  hasClassesOnDay = (day: Date) => {
    for (const item of this.allClasses) {
      const classTime = new Date(item.startTime);
      if (isEqualDates(day, classTime)) {
        return true;
      }
    }

    return false;
  };

  getConferenceNameForId = (id: string) => {
    const foundClass = this.allClasses.find(lesson => lesson.id == id);
    return (foundClass?.subject ?? '') + ' ' + (foundClass?.group ?? '');
  };

  getClasses = async () => {
    try {
      const days = this.getDatesToLoadClasses(this.currentDate);

      const data = await this.classApiManager.getClasses(
        dateFormatForApiRequest(days[0]),
        dateFormatForApiRequest(days[days.length - 1]),
      );

      this.setAllClasses(data);
      this.mapAndSetClasses(data, new Date(this.currentDate));
    } catch (err) {
      console.log(err);
    }
  };

  deleteLesson = async (idToDelete: string, deleteTypeId: DeleteTypeId) => {
    try {
      await this.classApiManager.deleteLesson(idToDelete, deleteTypeId);
    } catch (error) {
      console.log(error);
    }
  };

  getRecording = async (classId: string) => {
    try {
      const {message} = await this.classApiManager.getRecording(classId);

      if (message) {
        this.setIsRecordingRequest(true);

        const success = strings.successes.successRecording;
        this.popUpStore.setSuccess(success);
      }
    } catch (err) {
      const error = strings.errors.errorRecording;
      this.popUpStore.setError(error);
    }
  };

  setIsRecordingRequest(value: boolean) {
    this.isRecordingRequest = value;
  }

  setCurrentDateByDay = (day: Date) => {
    this.setCurrentDate(moment(day).format(constants.CALENDAR.homeFormat));
  };

  setCurrentDate = (date: string): void => {
    this.currentDate = date;
    this.mapAndSetClasses(this.allClasses, new Date(date));
  };

  setDayIndex = (day: number): void => {
    this.dayIndex = day;
  };

  setWeekNumber = (week: number): void => {
    this.weekNumber = week;
  };

  setLessonList = (list: SectionListFormat[]): void => {
    this.lessonList = list;
  };

  setAnalyticsList = (list: StudentScheduleFormat[]): void => {
    this.analyticLessonList = list;
  };

  setEventsList = (list: EventList[]): void => {
    this.eventList = list;
  };

  setAllClasses = (data: Class[]): void => {
    this.allClasses = data;
  };

  mapAndSetEventListForWeb = () => {
    const data = this.allClasses;
    const eventList: EventList[] = [];

    data.map(item => {
      const startTime = new Date(item.startTime);
      const endTime = new Date(
        startTime.getTime() + item.duration * constants.MILIS_IN_MINUTE,
      );
      const title = `${item.subject} ${
        strings.homeScreen.lesson
      } ${formatDateToTime(startTime)}`;

      eventList.push({start: startTime, end: endTime, title: title});
    });

    this.setEventsList(eventList);
  };

  mapAndSetClasses = (data: Class[], selectedDate: Date) => {
    const lessonList: SectionListFormat[] = [];

    const currentClasses: SectionListFormat = {
      title: strings.homeScreen.current,
      data: [],
    };

    const scheduledClasses: SectionListFormat = {
      title: strings.homeScreen.scheduled,
      data: [],
    };

    data.map(item => {
      const classDate = new Date(item.startTime);
      const classEndTime = addMinutes(item.startTime, item.duration);

      if (isEqualDates(classDate, selectedDate)) {
        const currentDate = new Date();
        const currentMinutes =
          currentDate.getHours() * constants.MINUTES_IN_HOUR +
          currentDate.getMinutes();
        const classMinutes =
          classDate.getHours() * constants.MINUTES_IN_HOUR +
          classDate.getMinutes();

        if (
          isEqualDates(classDate, currentDate) &&
          currentMinutes >= classMinutes &&
          currentMinutes < classMinutes + item.duration
        ) {
          currentClasses.data.push({
            sessionId: item.id,
            scheduleId: item.scheduleId ? item.scheduleId : '',
            title: item.subject,
            classMarking: item.group,
            classDate: classDate.toString(),
            scheduledTime: formatDateToTime(classDate),
            status: item.analyticsStatus,
            classEndTime,
            isRecordingNeeded: item.isRecordingNeeded,
          });
        } else {
          scheduledClasses.data.push({
            sessionId: item.id,
            scheduleId: item.scheduleId ? item.scheduleId : '',
            title: item.subject,
            classMarking: item.group,
            classDate: classDate.toString(),
            scheduledTime: formatDateToTime(classDate),
            status: item.analyticsStatus,
            classEndTime,
            isRecordingNeeded: item.isRecordingNeeded,
          });
        }
      }
    });

    if (currentClasses.data.length > 0) {
      lessonList.push(currentClasses);
    }

    if (scheduledClasses.data.length > 0) {
      lessonList.push(scheduledClasses);
    }

    this.setLessonList(lessonList);
    this.mapLessonListToAnalyticsList();
  };

  mapLessonListToAnalyticsList = () => {
    this.setAnalyticsList([]);
    this.lessonList.map(item => {
      let analyticsLessonListTemp: StudentScheduleFormat[] = [];
      item.data.map(items => {
        analyticsLessonListTemp.push(items);
        this.setAnalyticsList(analyticsLessonListTemp);
      });
    });
  };

  private getDatesToLoadClasses = (date: string): string[] => {
    const nowDate = new Date(date);
    const currentMonth = nowDate.getMonth();
    let toMonth = currentMonth + 2;
    let toYear = nowDate.getFullYear();
    if (toMonth > 11) {
      toMonth = toMonth - 12;
      toYear++;
    }

    nowDate.setMonth(currentMonth - 1);
    nowDate.setDate(0);

    let dates = [];

    while (nowDate.getMonth() < toMonth || nowDate.getFullYear() < toYear) {
      dates.push(nowDate.toString());
      nowDate.setDate(nowDate.getDate() + 1);
    }

    dates.push(nowDate.toString());
    return dates;
  };
}
